import axios from 'axios';
import { ENV } from '../../../config/env';
import { AUTH_STORAGE_KEYS } from '../../../shared/auth/constants';
import type {
  CompanyAccountSettings,
  UpdateCompanyInfoRequest,
  UpdateEmailRequest,
  UpdatePasswordRequest,
  UpdateNotificationsRequest,
  DeleteAccountRequest,
  TerminateDeviceRequest,
  ApiResponse,
  Device,
  CompanyNotificationSettings,
} from '../types';

// All company settings use Company-Service (email/password handled via gRPC to Signup-Service internally)
const COMPANY_SERVICE_URL = ENV.COMPANY_SERVICE_URL || 'http://localhost:8001';

const companySettingsClient = axios.create({
  baseURL: COMPANY_SERVICE_URL,
  timeout: ENV.API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to company settings requests
companySettingsClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

class CompanySettingsApiService {
  /**
   * Fetch company account settings
   */
  async getAccountSettings(): Promise<CompanyAccountSettings> {
    try {
      const response = await companySettingsClient.get<ApiResponse<CompanyAccountSettings>>(
        '/company-settings/account'
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to fetch account settings');
    } catch (error: any) {
      console.error('❌ Get company account settings error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch account settings'
      );
    }
  }

  /**
   * Update company information (company name, website)
   */
  async updateCompanyInfo(
    data: UpdateCompanyInfoRequest
  ): Promise<ApiResponse<any>> {
    try {
      const response = await companySettingsClient.put<ApiResponse<any>>(
        '/company-settings/company-info',
        data
      );

      return response.data;
    } catch (error: any) {
      console.error('❌ Update company info error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to update company information'
      );
    }
  }

  /**
   * Update email address
   * Company-Service handles this via gRPC to Signup-Service internally
   */
  async updateEmail(data: UpdateEmailRequest): Promise<ApiResponse<any>> {
    try {
      const response = await companySettingsClient.put<ApiResponse<any>>(
        '/company-settings/email',
        data
      );

      return response.data;
    } catch (error: any) {
      console.error('❌ Update email error:', error);
      throw new Error(
        error.response?.data?.status?.msg ||
        error.response?.data?.message ||
        'Failed to update email address'
      );
    }
  }

  /**
   * Update password
   * Company-Service handles this via gRPC to Signup-Service internally
   */
  async updatePassword(data: UpdatePasswordRequest): Promise<ApiResponse<any>> {
    try {
      const response = await companySettingsClient.put<ApiResponse<any>>(
        '/company-settings/password',
        data
      );

      return response.data;
    } catch (error: any) {
      console.error('❌ Update password error:', error);
      throw new Error(
        error.response?.data?.status?.msg ||
        error.response?.data?.message ||
        'Failed to update password'
      );
    }
  }

  /**
   * Update notification settings
   */
  async updateNotificationSettings(
    data: UpdateNotificationsRequest
  ): Promise<ApiResponse<CompanyNotificationSettings>> {
    try {
      const response = await companySettingsClient.put<ApiResponse<CompanyNotificationSettings>>(
        '/company-settings/notifications',
        data
      );

      return response.data;
    } catch (error: any) {
      console.error('❌ Update notifications error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to update notification settings'
      );
    }
  }

  /**
   * Get active devices
   */
  async getDevices(): Promise<Device[]> {
    try {
      const response = await companySettingsClient.get<ApiResponse<Device[]>>(
        '/company-settings/devices'
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to fetch devices');
    } catch (error: any) {
      console.error('❌ Get devices error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch devices'
      );
    }
  }

  /**
   * Terminate a specific device session
   */
  async terminateDevice(data: TerminateDeviceRequest): Promise<ApiResponse<any>> {
    try {
      const response = await companySettingsClient.delete<ApiResponse<any>>(
        `/company-settings/devices/${data.deviceId}`
      );

      return response.data;
    } catch (error: any) {
      console.error('❌ Terminate device error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to terminate device session'
      );
    }
  }

  /**
   * Terminate all other device sessions
   */
  async terminateAllOtherSessions(): Promise<ApiResponse<any>> {
    try {
      const response = await companySettingsClient.post<ApiResponse<any>>(
        '/company-settings/devices/terminate-all'
      );

      return response.data;
    } catch (error: any) {
      console.error('❌ Terminate all sessions error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to terminate sessions'
      );
    }
  }

  /**
   * Delete account
   */
  async deleteAccount(data: DeleteAccountRequest): Promise<ApiResponse<any>> {
    try {
      const response = await companySettingsClient.delete<ApiResponse<any>>(
        '/company-settings/account',
        { data }
      );

      return response.data;
    } catch (error: any) {
      console.error('❌ Delete account error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to delete account'
      );
    }
  }
}

export const companySettingsApi = new CompanySettingsApiService();
